import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { driversService } from '../../../services/driversService';
import { DriverFilter } from '../../../models/drivers/driverFilter';
import { DriverShortDto } from '../../../models/drivers/driverShortDto';
import { useDebouncedValue } from '../hooks/useDebouncedValue';

interface DriversAutocompleteFormControlProps {
  name: string;
  companyId: number | null;
  medicalcheckupId?: number;
  label?: string;
  style?: React.CSSProperties;
  required?: boolean;
}

const DriversAutocompleteFormControl: React.FC<DriversAutocompleteFormControlProps> = ({ name, companyId, medicalcheckupId, label = 'Сотрудники', style, required }) => {
  const { control, setValue } = useFormContext();
  const [options, setOptions] = useState<DriverShortDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const debouncedSearchText = useDebouncedValue(inputValue, 400);
  const contextChangedRef = useRef(false);

  useEffect(() => {
    setInputValue('');
    setValue(name, []);
    contextChangedRef.current = true;
  }, [companyId, medicalcheckupId, name, setValue]);

  useEffect(() => {
    if (!companyId) {
      setOptions([]);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const fio = contextChangedRef.current ? null : (debouncedSearchText || null);
    contextChangedRef.current = false;

    const filter: DriverFilter & { medicalcheckupId?: number } = {
      companyId,
      page: 0,
      pageSize: 50,
      hideDismissed: true,
      employeeNumber: null,
      fio,
      ...(medicalcheckupId ? { medicalcheckupId } : {}),
    };

    driversService.getByFilter(filter)
      .then(r => {
        if (!cancelled) {
          setOptions(r.data.data.items);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setOptions([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [companyId, debouncedSearchText, medicalcheckupId]);

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: required ? 'Обязательное поле' : false }}
      render={({ field, fieldState }) => (
        <Autocomplete
          multiple
          options={options}
          getOptionLabel={option => `${option.lastName} ${option.firstName} ${option.middleName}`}
          getOptionKey={option => option.id}
          filterOptions={x => x}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={field.value || []}
          inputValue={inputValue}
          onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
          onChange={(_, value) => field.onChange(value)}
          loading={loading}
          noOptionsText="Не найдено"
          renderInput={params => (
            <TextField
              {...params}
              label={label}
              placeholder="Введите ФИО для поиска"
              variant="outlined"
              margin="dense"
              size="small"
              style={style}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              required={required}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          size="small"
          disabled={!companyId}
        />
      )}
    />
  );
};

export default DriversAutocompleteFormControl;
